import React, {Component} from "react";
import {Control, Field, Icon} from "bloomer";
import Card from "../../../elements/Card";
import Button from "../../../elements/Button";
import SendGAEvent, {GAEventActions, GAEventModels} from "../../../utils/SendGAEvent";
import ErrorUtils from "../../../utils/ErrorUtils";
import {toast} from "react-toastify";
import SectionName from "../../../elements/SectionName";
import InformationCard from "../../../elements/InformationCard";
import Spacer from "../../../elements/Spacer";
import InputWarning from "../../../elements/InputWarning";
import ModelContentContainer from "../../../elements/ModelContentContainer";
import {getGeneratedAvatar} from "../../../api/models/Avatar";

interface IAvatarModelState {
    loading: boolean;
    downloading: boolean;
    file: any,
    imageUrl: any,
    imageBase64: any,
    fileErrorMessage: string,
    result: any;
    errorMessage: string;
}

class AvatarModel extends Component<{}, IAvatarModelState> {
    public ALLOWED_FILE_TYPES: any = ['image/jpeg', 'image/png'];
    public ALLOWED_FILE_TYPES_STR: string = this.ALLOWED_FILE_TYPES.join(',');
    public fileInputRef: any = null;

    public fileInputStyle = {
        width: '100%'
    };

    public constructor(props: {}) {
        super(props);

        this.state = {
            loading: false,
            downloading: false,
            file: null,
            imageUrl: "",
            imageBase64: "",
            fileErrorMessage: "",
            result: "",
            errorMessage: ""
        };
    }

    public async componentDidMount() {
        SendGAEvent(GAEventActions.VIEW_MODEL, GAEventModels.AVATAR_GENERATOR);
    }

    public downloadImage = (e: any) => {
        e.target.blur();

        if (!this.state.loading && this.state.result) {
            this.setState({downloading: true}, () => {
                setTimeout(() => {
                    const element = document.createElement("a");
                    element.href = this.state.result;
                    element.download = `avatar-${new Date().getTime()}.png`;
                    document.body.appendChild(element);
                    element.click();
                    document.body.removeChild(element);

                    this.setState({downloading: false});
                }, 1000);
            });
        }
    };

    public handleFileChange = (e: any) => {
        this.setState({
            fileErrorMessage: ""
        });

        const file = e.target.files[0];
        if (file) {
            if (this.ALLOWED_FILE_TYPES.includes(file.type)) {

                const url = URL.createObjectURL(file);
                this.setState({
                    file,
                    imageUrl: url
                }, () => {
                    const reader = new FileReader();
                    reader.onloadend = () => {
                        this.setState({
                            imageBase64: reader.result
                        });
                    };
                    reader.readAsDataURL(file);
                });
            } else {
                this.setState({
                    file: null,
                    imageUrl: "",
                    imageBase64: "",
                    fileErrorMessage: "The model only accepts JPEG or PNG format files."
                });
            }
        }
    };

    public onSubmit = async (e: any) => {
        e.target.blur();

        let valid = true;

        if (this.state.loading) {
            valid = false;
        }

        if (!this.state.file || !this.state.imageBase64 || this.state.fileErrorMessage) {
            if (!this.state.file || !this.state.imageBase64) {
                this.setState({
                    fileErrorMessage: "Please select image."
                });
            }

            valid = false;
        }

        if (!valid) {
            this.setState({
                result: ""
            });
            return;
        }

        this.setState({
            loading: true,
            result: "",
            errorMessage: ""
        });

        let errorOccurred = false;

        const formData = new FormData();
        formData.append("photo", this.state.file);

        const response = await getGeneratedAvatar(formData).catch((error) => {
            if (ErrorUtils.requestLimitExceeded(error.response)) {
                this.setState({loading: false});
                toast.error(ErrorUtils.messageContent.REQUEST_LIMIT_EXCEEDED);
            } else {
                this.setState({
                    loading: false,
                    errorMessage: "Couldn't process image."
                });
            }
            errorOccurred = true;
        });

        if (errorOccurred) {
            this.setState({loading: false});
            return;
        }

        if (response && response.data) {
            const blob = new Blob([response.data], {type: 'image/png'});
            this.setState({
                loading: false,
                result: URL.createObjectURL(blob)
            });
        } else {
            this.setState({
                loading: false,
                errorMessage: "Couldn't process image."
            });
        }

        SendGAEvent(GAEventActions.CALL_MODEL, GAEventModels.AVATAR_GENERATOR);
    };

    public resetState = async (e: any) => {
        e.target.blur();

        this.setState({
            loading: false,
            file: null,
            imageUrl: "",
            imageBase64: "",
            fileErrorMessage: "",
            result: "",
            errorMessage: "",
        });

        if (this.fileInputRef) {
            this.fileInputRef.value = "";
        }
    };

    public render() {
        return (
            <React.Fragment>
                <ModelContentContainer>
                    <Card>
                        <Field>
                            <Control>
                                <SectionName>Select Image:</SectionName>
                                <input
                                    ref={(el) => this.fileInputRef = el}
                                    type="file"
                                    accept={this.ALLOWED_FILE_TYPES_STR}
                                    onChange={this.handleFileChange}
                                    style={this.fileInputStyle}
                                />
                                {
                                    this.state.fileErrorMessage ? <div>
                                        <InputWarning>{this.state.fileErrorMessage}</InputWarning>
                                    </div> : <></>
                                }
                                {
                                    !this.state.fileErrorMessage && this.state.file && this.state.imageUrl ?
                                        <div style={{
                                            margin: '20px',
                                            textAlign: 'center'
                                        }}>
                                            <img
                                                alt="Image"
                                                src={this.state.imageUrl}
                                            />
                                        </div> : <></>
                                }
                            </Control>
                        </Field>
                        <Spacer/>
                        <div>
                            <Button onClick={this.onSubmit}>Submit</Button>
                            <Button onClick={this.resetState} style={{marginLeft: "5px"}}>Reset</Button>
                        </div>
                        {
                            this.state.loading && <div style={{textAlign: 'center'}}>
                                <Spacer/>
                                <span>Loading...</span>
                                <Icon className="fas fa-spinner fa-spin"/>
                            </div>
                        }
                        <Spacer/>
                        {
                            !this.state.loading && this.state.result ? <div>
                                <SectionName>Output:</SectionName>
                                <InformationCard>
                                    <div style={{
                                        margin: '20px',
                                        textAlign: 'center',
                                    }}>
                                        <img
                                            alt="Avatar"
                                            src={this.state.result}
                                        />
                                    </div>
                                </InformationCard>
                                <Spacer/>
                                <div style={{textAlign: 'center'}}>
                                    {
                                        this.state.downloading ? <div>
                                            <span>Downloading...</span>
                                            <Icon className="fas fa-spinner fa-spin"/>
                                        </div> : <Button
                                            title="Download Image"
                                            style={{ marginRight: "5px" }}
                                            onClick={(e: any) => {
                                                this.downloadImage(e);
                                            }}
                                        >
                                            <Icon className="fas fa-download" style={{marginRight: '5px'}}/>Download
                                        </Button>
                                    }
                                </div>
                                <Spacer/>
                            </div> : <></>
                        }
                        {
                            !this.state.loading && this.state.errorMessage ? <div>
                                {this.state.errorMessage}
                            </div> : <></>
                        }
                    </Card>
                </ModelContentContainer>
            </React.Fragment>
        );
    }
}

export default AvatarModel;


// WEBPACK FOOTER //
// ./src/routes/Dashboard/Avatar/AvatarModel.tsx